import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardHeader, CardContent, PaginatedTable } from '../../components/ui';
import type { PaginationState } from '../../components/ui';
import { PageHeader } from '@/components/layout/PageHeader';
import { adminBackofficeApi } from '../../services/adminBackoffice';

interface SupportItem {
  id: string;
  subject: string;
  status: string;
  type: string;
  userSummary?: { id: string; email: string; firstName: string; lastName: string } | null;
  academyName?: string | null;
  createdAt: string | Date;
}

export default function SupportListPage() {
  const [items, setItems] = useState<SupportItem[]>([]);
  const [pagination, setPagination] = useState<PaginationState | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    adminBackofficeApi
      .getSupportRequests({ page, limit: 20 })
      .then((data) => {
        if (data.success && data.data) {
          setItems(data.data.requests ?? []);
          setPagination(data.data.pagination ?? null);
        } else setError(data.error || 'Failed to load');
      })
      .catch((err) => setError(err?.response?.data?.error || err?.message || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [page]);

  if (error && items.length === 0) {
    return (
      <div>
        <PageHeader
          title="Support tickets"
          help={<p>All support requests from academy owners and users.</p>}
        />
        <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-900/20 p-4 text-red-700 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="Support tickets"
        help={<p>All support requests from academy owners and users.</p>}
      />
      <Card>
        <CardHeader>
          <h2 className="text-lg font-medium">All support requests</h2>
        </CardHeader>
        <CardContent>
          <PaginatedTable<SupportItem>
            columns={[
              { key: 'subject', header: 'Subject', render: (r) => r.subject },
              { key: 'status', header: 'Status', render: (r) => r.status },
              { key: 'type', header: 'Type', render: (r) => r.type },
              {
                key: 'user',
                header: 'User',
                render: (r) =>
                  r.userSummary
                    ? `${r.userSummary.firstName} ${r.userSummary.lastName} (${r.userSummary.email})`
                    : '–',
              },
              { key: 'academy', header: 'Academy', render: (r) => r.academyName ?? '–' },
              {
                key: 'created',
                header: 'Created',
                render: (r) => (r.createdAt ? new Date(r.createdAt).toLocaleString() : '–'),
              },
              {
                key: 'actions',
                header: 'Actions',
                render: (r) => (
                  <Link to={`/backoffice/support/${r.id}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                    View
                  </Link>
                ),
              },
            ]}
            data={items}
            pagination={pagination}
            loading={loading}
            emptyMessage="No support requests."
            onPageChange={setPage}
          />
        </CardContent>
      </Card>
    </div>
  );
}
